import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  Plus,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Edit,
} from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { useAppointments } from "../../hooks/useAppointments";

dayjs.locale("fr");

type StatusFilter = "PENDING" | "APPROVED" | "CANCELLED" | "REJECTED" | "";

interface Filters {
  status: StatusFilter;
  date: string;
  specialty: string;
}

const AppointmentsPage: React.FC = () => {
  const { data: appointments = [], isLoading, error: queryError } = useAppointments();
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    status: "",
    date: "",
    specialty: "",
  });

  const error = queryError ? (queryError as any).message : null;

  const filteredAppointments = useMemo(() => {
    return appointments.filter((appointment) => {
      const doctorName = `${appointment.doctor?.firstName ?? ""} ${appointment.doctor?.lastName ?? ""
        }`.toLowerCase();
      const specialty = appointment.doctor?.specialty?.toLowerCase() || "";
      const formattedDate = dayjs(appointment.appointmentDateTime).format(
        "DD/MM/YYYY"
      );

      // Filtre de recherche
      const matchesSearch =
        doctorName.includes(searchTerm.toLowerCase()) ||
        specialty.includes(searchTerm.toLowerCase());

      // Filtres avancés
      const matchesStatus =
        !filters.status ||
        appointment.status.toLowerCase() === filters.status.toLowerCase();

      const matchesDate =
        !filters.date || formattedDate.includes(filters.date.padStart(10, "0"));

      const matchesSpecialty =
        !filters.specialty || specialty === filters.specialty.toLowerCase();

      return matchesSearch && matchesStatus && matchesDate && matchesSpecialty;
    });
  }, [appointments, searchTerm, filters]);

  const statusOptions: StatusFilter[] = [
    "PENDING",
    "APPROVED",
    "CANCELLED",
    "REJECTED",
  ];
  const specialtyOptions = [
    "Cardiologie",
    "Dermatologie",
    "Pédiatrie",
    "Ophtalmologie",
  ];

  const clearFilters = () => {
    setFilters({ status: "", date: "", specialty: "" });
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "CANCELLED":
        return "bg-red-100 text-red-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const translateStatus = (status: string): string => {
    const statusMap: Record<string, string> = {
      APPROVED: "Confirmé",
      PENDING: "En attente",
      CANCELLED: "Annulé",
      REJECTED: "Rejeté",
    };
    return statusMap[status] || status;
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <X className="h-5 w-5 text-red-500" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (
    !appointments.length &&
    !searchTerm &&
    !Object.values(filters).some(Boolean)
  ) {
    return (
      <div className="p-8 sm:p-12 text-center">
        <div className="mx-auto w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Calendar size={40} className="text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          Vous n'avez aucun rendez-vous
        </h3>
        <Link
          to="/dashboard/appointments/new"
          className="inline-flex items-center gap-2 mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus size={18} />
          Prendre un rendez-vous
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Mes Rendez-vous</h1>
        <Link
          to="/dashboard/appointments/new"
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors"
          aria-label="Créer un nouveau rendez-vous"
        >
          <Plus size={18} />
          Nouveau rendez-vous
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-grow">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
              aria-hidden="true"
            />
            <input
              type="text"
              placeholder="Rechercher par médecin ou spécialité..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Rechercher des rendez-vous"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            aria-expanded={showFilters}
            aria-controls="filters-section"
          >
            <Filter size={18} aria-hidden="true" />
            Filtres
            {showFilters ? (
              <ChevronUp size={18} aria-hidden="true" />
            ) : (
              <ChevronDown size={18} aria-hidden="true" />
            )}
          </button>
        </div>

        {showFilters && (
          <div id="filters-section" className="bg-gray-50 p-3 sm:p-4 rounded-lg mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="status-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Statut
                </label>
                <select
                  id="status-filter"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      status: e.target.value as StatusFilter,
                    })
                  }
                >
                  <option value="">Tous les statuts</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {translateStatus(status)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="date-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Date
                </label>
                <input
                  id="date-filter"
                  type="text"
                  placeholder="JJ/MM/AAAA"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  value={filters.date}
                  onChange={(e) =>
                    setFilters({ ...filters, date: e.target.value })
                  }
                />
              </div>
              <div>
                <label
                  htmlFor="specialty-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Spécialité
                </label>
                <select
                  id="specialty-filter"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  value={filters.specialty}
                  onChange={(e) =>
                    setFilters({ ...filters, specialty: e.target.value })
                  }
                >
                  <option value="">Toutes spécialités</option>
                  {specialtyOptions.map((spec) => (
                    <option key={spec} value={spec}>
                      {spec}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {(filters.status || filters.date || filters.specialty) && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                  aria-label="Réinitialiser les filtres"
                >
                  <X size={16} aria-hidden="true" />
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {filteredAppointments.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {filteredAppointments.map((appointment) => (
              <li
                key={appointment.id}
                className="p-4 sm:p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium"
                      aria-hidden="true"
                    >
                      {appointment.doctor?.firstName?.charAt(0)}
                      {appointment.doctor?.lastName?.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">
                        Dr. {appointment.doctor?.firstName}{" "}
                        {appointment.doctor?.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {appointment.doctor?.specialty || "Spécialité inconnue"}
                      </p>
                      {appointment.reason && (
                        <p className="text-sm text-gray-500 mt-1">
                          Motif: {appointment.reason}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar size={16} aria-hidden="true" />
                      <span>
                        {dayjs(appointment.appointmentDateTime).format(
                          "dddd D MMMM YYYY"
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock size={16} aria-hidden="true" />
                      <span>
                        {dayjs(appointment.appointmentDateTime).format("HH:mm")}
                      </span>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        appointment.status
                      )}`}
                    >
                      {translateStatus(appointment.status)}
                    </span>
                    <Link
                      to={`/appointments/new`}
                      className="text-indigo-600 hover:text-indigo-800 transition-colors"
                      aria-label="Modifier le rendez-vous"
                    >
                      <Edit size={18} />
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-8 sm:p-12 text-center">
            <div className="mx-auto w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Calendar
                size={40}
                className="text-gray-400"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              Aucun rendez-vous trouvé
            </h3>
            <p className="text-gray-500 mb-4">
              Essayez de modifier vos critères de recherche
            </p>
            <button
              onClick={clearFilters}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              Réinitialiser tous les filtres
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentsPage;
